export type DocumentType = "word" | "excel" | "pdf" | "powerpoint"
export const PDF_PREVIEW_MIME_TYPE = "application/pdf"

const documentExtensions: Record<DocumentType, string[]> = {
  word: ["doc", "docx", "odt", "rtf"],
  excel: ["xls", "xlsx", "ods"],
  pdf: ["pdf"],
  powerpoint: ["ppt", "pptx", "odp"]
}

const browserPreviewMimeTypes: Record<string, string> = {
  // Browsers may download text/csv; text/plain reliably opens the same bytes in a preview tab.
  csv: "text/plain;charset=utf-8",
  // HTML attachments must keep their document MIME type so browser previews render the page.
  htm: "text/html;charset=utf-8",
  html: "text/html;charset=utf-8",
  json: "application/json",
  md: "text/markdown",
  pdf: PDF_PREVIEW_MIME_TYPE,
  text: "text/plain",
  txt: "text/plain",
  xml: "application/xml"
}

const fileIconNames: Record<string, string> = {
  doc: "doc",
  docx: "docx",
  pdf: "pdf",
  xls: "xls",
  xlsx: "xls",
  ods: "xls",
  csv: "csv",
  key: "key",
  ppt: "pptx",
  pptx: "pptx",
  odp: "pptx",
  txt: "txt",
  json: "json",
  html: "html",
  htm: "html",
  xml: "xml",
  md: "md",
  mp4: "mp4",
  mov: "mov",
  avi: "avi",
  webm: "webm",
  zip: "zip",
  rar: "rar",
  jpg: "jpg",
  jpeg: "jpg",
  png: "png",
  gif: "gif",
  svg: "svg",
  webp: "webp"
}

export const filesNotOpenable = ["key", "zip", "rar", "avi", "unknown"]
/** Returns a normalized extension without including query or hash fragments. */
export function getFileExtension(fileName: string) {
  const normalizedName = fileName.split(/[?#]/, 1)[0]
  const extensionIndex = normalizedName.lastIndexOf(".")

  return extensionIndex < 0 ? "" : normalizedName.slice(extensionIndex + 1).toLowerCase()
}

/** Maps common office document extensions to the icon category shown in attachment cards. */
export function getDocumentType(fileName: string): DocumentType | undefined {
  const extension = getFileExtension(fileName)

  return (Object.keys(documentExtensions) as DocumentType[]).find(type =>
    documentExtensions[type].includes(extension)
  )
}

/** Returns the icon filename for a supported attachment extension. */
export function getFileIconName(fileName: string) {
  return fileIconNames[getFileExtension(fileName)]
}

export function checkNotOpenable(fileName: string) {
  const extension = getFileExtension(fileName)
  return Boolean(getFileIconName(fileName) || getDocumentType(fileName)) ? filesNotOpenable.includes(extension) : true
}

/** Returns the MIME type for document formats browsers can reliably display in a new tab. */
export function getBrowserPreviewMimeType(fileName: string) {
  return browserPreviewMimeTypes[getFileExtension(fileName)]
}

/** Returns true when an office attachment can be converted to PDF by the preview service. */
export function isPdfConversionPreviewable(fileName: string) {
  const documentType = getDocumentType(fileName)

  return Boolean(documentType && documentType !== "pdf")
}
